import { useEffect, useRef, useState } from 'react'

/**
 * null = snapshot every field from the current values; string[] = snapshot only those
 * dot-separated paths; function = compute the next baseline directly from the current one.
 */
type RebaselineRequest = string[] | ((currentBaseline: unknown) => unknown) | null

const getAtPath = (source: unknown, path: string): unknown =>
  path.split('.').reduce<unknown>(
    (acc, key) => (acc == null ? undefined : (acc as Record<string, unknown>)[key]),
    source,
  )

const setAtPath = (target: unknown, path: string, value: unknown): unknown => {
  const [head, ...rest] = path.split('.')
  const base = target != null && typeof target === 'object' ? (target as Record<string, unknown>) : {}
  return {
    ...base,
    [head]: rest.length ? setAtPath(base[head], rest.join('.'), value) : value,
  }
}

/**
 * Tracks whether form values have diverged from a baseline snapshot. Built for forms that
 * are seeded programmatically after load: formik.dirty is wrong for those, because seeding
 * via setValues / setFieldValue marks the form dirty before the user has touched anything,
 * and hand-rolled useState forms have no dirty flag at all.
 *
 * rebaseline() takes three forms:
 * - No arguments: snapshot every field from the current values. Only safe when the user
 *   cannot have touched the form, or when the whole form was just rewritten: synchronously
 *   in the open transition, or a seed that rewrites every field.
 * - Field paths, e.g. rebaseline(['conversionSourceId']): snapshot only those fields. Use
 *   for a seed that lands after a network round-trip, passing exactly the fields it wrote,
 *   so anything the user typed in the meantime stays dirty. Dot notation reaches into
 *   nested value objects.
 * - An updater, e.g. rebaseline(baseline => next): compute the next baseline directly,
 *   independent of the current values. Use when the clean state is not the current form
 *   state: baselining a save to the exact values that were submitted (keystrokes typed
 *   while the save was in flight stay dirty), or splicing one persisted deletion out of a
 *   list that also holds unsaved entries.
 *
 * Snapshot forms are applied on the commit AFTER the call, so every state update batched
 * with the request (the seeding setValues, async .then seeds, and so on) is included.
 *
 * `values` should be a plain serialisable object (formik.values, or an object literal of
 * the form's useState fields). Comparison is by JSON serialisation.
 */
export const useDirtyBaseline = (values: unknown): {
  isDirty: boolean,
  rebaseline: (request?: string[] | ((currentBaseline: unknown) => unknown)) => void,
} => {
  const [baseline, setBaseline] = useState(values)
  const [baselineTick, setBaselineTick] = useState(0)
  const pendingRequests = useRef<RebaselineRequest[]>([])

  // Apply queued rebaseline requests one commit after they were made, so state updates
  // batched with each request are reflected in the snapshot it takes.
  useEffect(() => {
    if (baselineTick === 0) return
    const requests = pendingRequests.current
    pendingRequests.current = []
    setBaseline((currentBaseline: unknown) =>
      requests.reduce((nextBaseline, request) => {
        if (request === null) return values
        if (typeof request === 'function') return request(nextBaseline)
        return request.reduce(
          (acc, path) => setAtPath(acc, path, getAtPath(values, path)),
          nextBaseline,
        )
      }, currentBaseline),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselineTick])

  const rebaseline = (request?: string[] | ((currentBaseline: unknown) => unknown)) => {
    pendingRequests.current.push(request ?? null)
    setBaselineTick(tick => tick + 1)
  }

  return { isDirty: JSON.stringify(values) !== JSON.stringify(baseline), rebaseline }
}
